import { requireElement } from "./Hud";

/**
 * O que os controles de toque mostram no momento:
 * - `city`     analogico + botoes de acao e chat
 * - `minigame` so voltar/ranking; a area de arraste sai da frente para o
 *              toque chegar ao canvas do Phaser (tocar = bater asa)
 * - `hidden`   nada (menus e overlays)
 */
export type TouchMode = "hidden" | "city" | "minigame";

/** Deslocamento maximo do knob a partir do centro, em pixels de tela. */
const STICK_RADIUS = 46;
/** Abaixo disso o toque conta como parado. */
const DEAD_ZONE = 7;
/** Piso da velocidade analogica: encostar no limite da dead zone ja anda direito. */
const MIN_SPEED = 0.35;

export interface Axis {
  x: number;
  y: number;
}

/**
 * Analogico virtual flutuante: nasce onde o dedo encosta, em qualquer ponto da
 * tela, e a base acompanha o dedo quando ele passa do raio maximo. A saida e
 * analogica (0 a 1), entao o avatar anda mais devagar com o dedo perto do
 * centro — o servidor entende magnitudes menores que 1 sem nenhuma mudanca.
 */
export class TouchControls {
  private readonly root = requireElement<HTMLDivElement>("touch");
  private readonly drag = requireElement<HTMLDivElement>("touch-drag");
  private readonly stick = requireElement<HTMLDivElement>("stick");
  private readonly knob = requireElement<HTMLDivElement>("stick-knob");
  private readonly cityButtons = requireElement<HTMLDivElement>("touch-city");
  private readonly gameButtons = requireElement<HTMLDivElement>("touch-game");
  private readonly interactBtn = requireElement<HTMLButtonElement>("btn-interact");
  private readonly chatBtn = requireElement<HTMLButtonElement>("btn-chat");
  private readonly gestureBtn = requireElement<HTMLButtonElement>("btn-gesture");
  private readonly boardBtn = requireElement<HTMLButtonElement>("btn-board");
  private readonly backBtn = requireElement<HTMLButtonElement>("btn-back");

  private active = false;
  private mode: TouchMode = "hidden";

  private pointerId: number | null = null;
  private originX = 0;
  private originY = 0;
  private axisX = 0;
  private axisY = 0;

  private handlers: {
    interact?: () => void;
    chat?: () => void;
    gesture?: () => void;
    board?: () => void;
    back?: () => void;
  } = {};

  constructor() {
    // "pointer: coarse" e o sinal certo: o ponteiro principal e o dedo. Ainda
    // assim ativamos no primeiro toque, para cobrir hibridos e emuladores.
    if (matchMedia("(pointer: coarse)").matches) {
      this.activate();
    } else {
      window.addEventListener("touchstart", () => this.activate(), { once: true, passive: true });
    }

    this.bindStick();
    this.bindButtons();
  }

  /** true quando os controles de toque estao visiveis (o teclado segue valendo). */
  get enabled(): boolean {
    return this.active;
  }

  /** Direcao atual do analogico. (0, 0) quando ninguem esta arrastando. */
  get axis(): Axis {
    return { x: this.axisX, y: this.axisY };
  }

  private activate(): void {
    if (this.active) return;
    this.active = true;
    document.body.classList.add("touch");
    this.applyMode();
  }

  // ------------------------------------------------------------- analogico

  private bindStick(): void {
    this.drag.addEventListener("pointerdown", (event) => {
      if (this.mode !== "city" || this.pointerId !== null) return;
      // Com mouse em tela sensivel ao toque, ignoramos: teclado ja resolve.
      if (event.pointerType === "mouse" && !this.active) return;

      this.pointerId = event.pointerId;
      try {
        // Segura o ponteiro para o arraste continuar valendo mesmo se o dedo
        // sair da area. Alguns navegadores recusam ponteiros ja liberados.
        this.drag.setPointerCapture(event.pointerId);
      } catch {
        /* segue sem captura: os eventos ainda chegam enquanto o dedo estiver aqui */
      }
      this.originX = event.clientX;
      this.originY = event.clientY;

      this.placeStick();
      this.moveKnob(0, 0);
      this.stick.classList.remove("hidden");
      event.preventDefault();
    });

    this.drag.addEventListener("pointermove", (event) => {
      if (event.pointerId !== this.pointerId) return;

      let dx = event.clientX - this.originX;
      let dy = event.clientY - this.originY;
      const distance = Math.hypot(dx, dy);

      if (distance > STICK_RADIUS) {
        // A base persegue o dedo: arrastos longos nao "saturam" o analogico
        // longe do lugar onde a mao esta.
        const overshoot = distance - STICK_RADIUS;
        this.originX += (dx / distance) * overshoot;
        this.originY += (dy / distance) * overshoot;
        dx = (dx / distance) * STICK_RADIUS;
        dy = (dy / distance) * STICK_RADIUS;
        this.placeStick();
      }

      this.moveKnob(dx, dy);
      this.setAxis(dx, dy);
      event.preventDefault();
    });

    const release = (event: PointerEvent) => {
      if (event.pointerId !== this.pointerId) return;
      this.pointerId = null;
      this.axisX = 0;
      this.axisY = 0;
      this.stick.classList.add("hidden");
    };

    this.drag.addEventListener("pointerup", release);
    this.drag.addEventListener("pointercancel", release);
    this.drag.addEventListener("lostpointercapture", release);
  }

  private setAxis(dx: number, dy: number): void {
    const magnitude = Math.hypot(dx, dy);
    if (magnitude < DEAD_ZONE) {
      this.axisX = 0;
      this.axisY = 0;
      return;
    }
    const range = STICK_RADIUS - DEAD_ZONE;
    const normalized = Math.min((magnitude - DEAD_ZONE) / range, 1);
    const speed = Math.max(MIN_SPEED, normalized);
    this.axisX = (dx / magnitude) * speed;
    this.axisY = (dy / magnitude) * speed;
  }

  private placeStick(): void {
    this.stick.style.transform = `translate3d(${this.originX}px, ${this.originY}px, 0)`;
  }

  private moveKnob(dx: number, dy: number): void {
    this.knob.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
  }

  // ---------------------------------------------------------------- botoes

  private bindButtons(): void {
    const tap = (button: HTMLButtonElement, run: () => (() => void) | undefined) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        run()?.();
      });
    };

    tap(this.interactBtn, () => this.handlers.interact);
    tap(this.chatBtn, () => this.handlers.chat);
    tap(this.gestureBtn, () => this.handlers.gesture);
    tap(this.boardBtn, () => this.handlers.board);
    tap(this.backBtn, () => this.handlers.back);
  }

  onInteract(handler: () => void): void {
    this.handlers.interact = handler;
  }

  onChat(handler: () => void): void {
    this.handlers.chat = handler;
  }

  onGesture(handler: () => void): void {
    this.handlers.gesture = handler;
  }

  onLeaderboard(handler: () => void): void {
    this.handlers.board = handler;
  }

  onBack(handler: () => void): void {
    this.handlers.back = handler;
  }

  /** Mostra o botao de acao com o rotulo do objeto proximo, ou o esconde. */
  setInteract(label: string | null): void {
    if (!label) {
      this.interactBtn.classList.add("hidden");
      return;
    }
    this.interactBtn.textContent = label;
    this.interactBtn.classList.remove("hidden");
  }

  /** Fantasmas nao conversam: o botao some para nao prometer o que nao existe. */
  setChatAvailable(available: boolean): void {
    this.chatBtn.classList.toggle("hidden", !available);
  }

  setGestureAvailable(available: boolean): void {
    this.gestureBtn.classList.toggle("hidden", !available);
  }

  setMode(mode: TouchMode): void {
    if (mode === this.mode) return;
    this.mode = mode;

    // Soltar o analogico ao trocar de contexto evita o avatar seguir andando.
    this.pointerId = null;
    this.axisX = 0;
    this.axisY = 0;
    this.stick.classList.add("hidden");

    this.applyMode();
  }

  private applyMode(): void {
    this.root.classList.toggle("hidden", !this.active || this.mode === "hidden");
    this.cityButtons.classList.toggle("hidden", this.mode !== "city");
    this.gameButtons.classList.toggle("hidden", this.mode !== "minigame");

    // No minigame a area de arraste sai do caminho: o toque precisa chegar ao
    // canvas para o passaro bater asa.
    this.drag.style.pointerEvents = this.mode === "city" ? "auto" : "none";
    if (this.mode !== "city") this.setInteract(null);
  }
}
